import Box from '@mui/material/Box';
import { WS_COLOR, WS_CONTROL, WS_STATUS } from './tokens';

export interface SideNavItem {
  key: string;
  label: string;
  /** A short trailing value: "3 of 5", a count, a state word. Words, not colour. */
  trailing?: string;
}

export interface SideNavProps {
  label: string;
  items: readonly SideNavItem[];
  value: string;
  onChange: (key: string) => void;
}

/**
 * Section navigation for a form or settings area. A real list of buttons, the current one
 * marked with `aria-current` and the info pair.
 */
export function SideNav({ label, items, value, onChange }: SideNavProps) {
  return (
    <Box component="nav" aria-label={label} sx={{ minWidth: 0 }}>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5 }}>
        {items.map((item) => {
          const current = item.key === value;
          return (
            <li key={item.key}>
              <Box
                component="button"
                type="button"
                aria-current={current ? 'true' : undefined}
                onClick={() => onChange(item.key)}
                sx={{
                  width: '100%',
                  minHeight: WS_CONTROL.hitArea,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  px: 3,
                  border: 0,
                  borderRadius: '22px',
                  font: 'inherit',
                  fontSize: 14,
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontWeight: current ? 600 : 400,
                  color: current ? WS_STATUS.info.ink : WS_COLOR.text,
                  backgroundColor: current ? WS_STATUS.info.fill : 'transparent',
                  '&:hover': { backgroundColor: current ? WS_STATUS.info.fill : WS_COLOR.tonal },
                }}
              >
                <span>{item.label}</span>
                {item.trailing ? (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: current ? WS_STATUS.info.ink : WS_COLOR.textMuted,
                    }}
                  >
                    {item.trailing}
                  </Box>
                ) : null}
              </Box>
            </li>
          );
        })}
      </Box>
    </Box>
  );
}
